import {
  CommonComponentPresentation,
  CommonKVNode,
  CommonKVStoreState,
  CommonPresentationItem,
  PlaygroundSeedMessage,
} from "../../../types";
import { applyItemEdit } from "../../../playground/playgroundComponentState";

// One kv node editor apply: any of a replacement value and a rename of the
// key's own last segment, applied together.
export type KVNodeEdit = { value?: unknown; segment?: string };

const keyOf = (message: PlaygroundSeedMessage): string | null =>
  "key" in message && typeof message.key === "string" ? message.key : null;

export const applyKVNodeEdit = (
  messages: Array<PlaygroundSeedMessage>,
  key: string,
  edit: KVNodeEdit,
  delimiter: string | null
): Array<PlaygroundSeedMessage> => {
  const parts = delimiter !== null ? key.split(delimiter) : [key];
  const renamedKey =
    edit.segment !== undefined
      ? [...parts.slice(0, -1), edit.segment].join(delimiter ?? "")
      : key;
  const isUnderKey = (candidate: string) =>
    delimiter !== null && candidate.startsWith(key + delimiter);

  let hasOwnMessage = false;
  const next = messages.map((message) => {
    const messageKey = keyOf(message);
    if (messageKey === key) {
      hasOwnMessage = true;
      const rekeyed = applyItemEdit(message, { key: renamedKey });
      if (!("value" in edit)) {
        return rekeyed;
      }
      const revalued: Record<string, unknown> = {
        ...rekeyed,
        value: edit.value,
      };
      return revalued as PlaygroundSeedMessage;
    }
    if (messageKey !== null && renamedKey !== key && isUnderKey(messageKey)) {
      return applyItemEdit(message, {
        key: renamedKey + messageKey.slice(key.length),
      });
    }
    return message;
  });
  if (hasOwnMessage || !("value" in edit)) {
    return next;
  }

  const added: Record<string, unknown> = {
    message_type: "KVMessage",
    source: "user",
    key: renamedKey,
    value: edit.value,
  };
  // Sibling order is first-encounter order in the state build, so a
  // brand-new key lands ahead of its parent's existing children — the
  // root's, for a top-level key — where the add row drafts it. A structural
  // key gaining its first value stays appended, keeping its established
  // slot.
  const isNewKey = !messages.some((message) => {
    const messageKey = keyOf(message);
    return messageKey !== null && isUnderKey(messageKey);
  });
  const parentPrefix =
    delimiter !== null && parts.length > 1
      ? parts.slice(0, -1).join(delimiter) + delimiter
      : "";
  const insertAt = messages.findIndex((message) => {
    const messageKey = keyOf(message);
    return messageKey !== null && messageKey.startsWith(parentPrefix);
  });
  if (isNewKey && insertAt >= 0) {
    return [
      ...next.slice(0, insertAt),
      added as PlaygroundSeedMessage,
      ...next.slice(insertAt),
    ];
  }
  return [...next, added as PlaygroundSeedMessage];
};

// Deleting a kv node takes its whole subtree: the key's own message and
// every message whose key extends it through the delimiter.
export const deleteKVSubtree = (
  messages: Array<PlaygroundSeedMessage>,
  key: string,
  delimiter: string | null
): Array<PlaygroundSeedMessage> =>
  messages.filter((message) => {
    const messageKey = keyOf(message);
    return (
      messageKey === null ||
      (messageKey !== key &&
        !(delimiter !== null && messageKey.startsWith(key + delimiter)))
    );
  });

// Present a kv-store component's seed messages as the shared visualization's
// state tree, plus the presentation carrying its moment links. Each
// KVMessage holds one leaf: its full delimiter-joined key path and the
// final value.
export const seedMessagesToKVStoreState = (
  componentId: string,
  messages: Array<PlaygroundSeedMessage>,
  delimiter: string | null
): {
  componentState: CommonKVStoreState;
  componentPresentation: CommonComponentPresentation;
} => {
  const data: Record<string, CommonKVNode> = {};
  const items: Array<CommonPresentationItem> = [];
  messages.forEach((message, index) => {
    const key = keyOf(message);
    if (key === null) {
      return;
    }
    const path = delimiter ? key.split(delimiter) : [key];
    let cursor = data;
    let node: CommonKVNode | undefined;
    path.forEach((segment, depth) => {
      node = cursor[segment] ?? (cursor[segment] = {});
      if (depth < path.length - 1) {
        node.nodes = node.nodes ?? {};
        cursor = node.nodes;
      }
    });
    if (!node) {
      return;
    }
    node.value = "value" in message ? message.value : undefined;
    if ("agency_tags" in message && message.agency_tags) {
      node.agency_tags = message.agency_tags;
    }
    if (message.origin_moment_id != null && message.origin_session_id) {
      const momentUuid = `seed-${index}`;
      node.moment_uuid = momentUuid;
      items.push({
        moment_uuid: momentUuid,
        moment_id: message.origin_moment_id,
        session_id: message.origin_session_id,
        source: message.source ?? "user",
        content: String(node.value ?? ""),
      });
    }
  });
  return {
    componentState: {
      id: componentId,
      type: "kv_store",
      state: { delimiter, data },
    },
    componentPresentation: { id: componentId, element: "data", items },
  };
};
